import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTicketUpdateActions } from "../../../../Ticket/components/useTicketUpdateActions";
import { GroupByTypeEnum } from "../../../types";
import { ticketGroupsActions } from "../redux/slice";
import { selectDraftBoardForm } from "../../../components/Settings/redux/selector";
import { ITickets } from "../types";

export const useTicketFieldUpdate = (
  ticketId: string | string[],
  projectId: string,
) => {
  const dispatch = useDispatch();
  const updates = useTicketUpdateActions({ projectId });

  const { groupBy, subGroupBy } = useSelector(selectDraftBoardForm);

  const ticketIds = useMemo(
    () => (Array.isArray(ticketId) ? ticketId : [ticketId]),
    [ticketId],
  );

  const localMap = useMemo(
    () => ({
      [GroupByTypeEnum.ASSIGNEE]: (value: string) => ({ assignedTo: value }),
      [GroupByTypeEnum.STATUS]: (value: string) => ({ status: value }),
      [GroupByTypeEnum.TICKET_TYPE]: (value: string) => ({ type: value }),
      [GroupByTypeEnum.PRIORITY]: (value: string) => ({ priority: value }),
      [GroupByTypeEnum.DUE_DATE]: (value: string) => ({ dueDate: value }),
      [GroupByTypeEnum.TAGS]: (value: string) => ({ tags: value }),
      [GroupByTypeEnum.EFFORTS_ESTIMATION]: (value: string) => ({
        effortEstimated: value,
      }),
    }),
    [],
  );

  const serverMap = useMemo(
    () => ({
      [GroupByTypeEnum.ASSIGNEE]: updates.assigneeUpdate,
      [GroupByTypeEnum.STATUS]: updates.statusUpdate,
      [GroupByTypeEnum.TICKET_TYPE]: updates.ticketTypeUpdate,
      [GroupByTypeEnum.PRIORITY]: updates.priorityUpdate,
      [GroupByTypeEnum.DUE_DATE]: updates.dueDateUpdate,
      [GroupByTypeEnum.TAGS]: updates.ticketTagsUpdate,
      [GroupByTypeEnum.EFFORTS_ESTIMATION]: updates.effortEstimationUpdate,
    }),
    [updates],
  );

  const handleUpdate = useCallback(
    (field: GroupByTypeEnum, value: string | string[], extra?: any) => {
      let localUpdate: Partial<ITickets> = {};

      if (field === GroupByTypeEnum.TAGS) {
        dispatch(
          ticketGroupsActions.toggleTicketTags({
            ticketIds,
            tagIds: Array.isArray(value) ? value : [value],
          }),
        );
      } else {
        localUpdate = localMap[field]?.(value as string) || {};
        const isGroupByField = field === groupBy || field === subGroupBy;

        if (isGroupByField) {
          dispatch(
            ticketGroupsActions.updateTicketFieldAndShift({
              ticketIds,
              updates: localUpdate,
              groupBy,
              subGroupBy: subGroupBy ?? null,
            }),
          );
        } else {
          dispatch(
            ticketGroupsActions.updateTicketFieldLocally({
              ticketIds,
              updates: localUpdate,
            }),
          );
        }
      }

      serverMap[field]?.(value, ticketId, extra);
    },
    [dispatch, ticketIds, ticketId, localMap, serverMap, groupBy, subGroupBy],
  );

  return { handleUpdate };
};
